// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The pdf.js adapter — as thin as it can be, because everything interesting
 * happens in `extract.ts`, which is pure and testable.
 *
 * ── The LEGACY build, not the modern one ─────────────────────────────────────
 * `import * as pdfjs from 'pdfjs-dist'` resolves to `build/pdf.mjs`, which
 * contains, unguarded at module top level, `if (typeof Iterator.prototype.join
 * !== "function")`. `typeof` protects a bare identifier; it does NOT protect a
 * property access on one. Global `Iterator` arrived in Safari 18.4, so on every
 * iPhone below that the import throws `ReferenceError: Iterator is not defined`
 * before a line of chunkforge runs — a white page, not a degraded one. The same
 * bundle also uses bare `Promise.withResolvers` (Safari 17.4), `Promise.try`,
 * `Uint8Array.prototype.toHex` and `Math.sumPrecise`. Measured against a real
 * PDF in an environment lacking those builtins, the modern build failed in a
 * cascade and the legacy build produced byte-identical output with no polyfills.
 * The cost is about 16 kB gzipped on the worker. That is the whole trade.
 *
 * ── cMaps, or CJK documents come back silently empty ─────────────────────────
 * Without `cMapUrl`, a PDF using a predefined CMap encoding (`/90ms-RKSJ-H` and
 * friends — the ordinary shape of a Japanese contract exported from Word)
 * resolves, paginates, and returns `items: []`. No error, no rejection, just a
 * console warning and an empty document. For a tool whose output is a retrieval
 * score over a corpus, that is a hole in the denominator presented as a result.
 * The maps are served from this origin, are fetched only when a document
 * actually needs one, and a Latin-only corpus fetches none of them.
 *
 * The worker is self-hosted through `new URL(..., import.meta.url)` so no CDN
 * appears anywhere, in the bundle or in the content-security policy.
 */

import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import type { ExtractedDoc, RawItem } from './extract.ts';
import { itemsFromTextContent, layout } from './extract.ts';

let workerConfigured = false;

function configureWorker(): void {
  if (workerConfigured) return;
  workerConfigured = true;
  try {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
      import.meta.url,
    ).href;
  } catch {
    /* Node and the test runner fall back to pdf.js's in-process worker. */
  }
}

export interface PdfProgress {
  page: number;
  pages: number;
}

export interface PdfResult extends ExtractedDoc {
  /** Pages that produced no text items at all. */
  emptyPages: number;
}

/**
 * Read every page's text layer and hand the positioned runs to the layout
 * engine.
 *
 * A PDF with no text layer is a real and common answer, not an error: a scan has
 * pictures of words and nothing to chunk. It comes back with
 * `hasTextLayer: false` so the interface can say so plainly, rather than
 * presenting an empty corpus as a working one. Pages that individually produced
 * nothing are counted too, because a document that is 90% scanned is a hole in
 * any score computed over it.
 */
export async function extractPdf(
  data: ArrayBuffer,
  name: string,
  onProgress?: (p: PdfProgress) => void,
): Promise<PdfResult> {
  configureWorker();
  const task = pdfjs.getDocument({
    data: new Uint8Array(data),
    // Same-origin, lazily fetched, and only for documents that use a predefined
    // CMap encoding. Everything else fetches zero bytes.
    cMapUrl: new URL('cmaps/', document.baseURI).href,
    cMapPacked: true,
    // No fonts, no images, no external resources of any kind.
    //
    // `isEvalSupported` is deliberately absent: pdf.js 6 removed it, and the
    // v4-era incantation the rest of the fleet uses no longer type-checks. This
    // page's content-security policy has no 'unsafe-eval' regardless.
    disableFontFace: true,
    useSystemFonts: false,
  });
  const doc = await task.promise;
  try {
    const items: RawItem[] = [];
    let emptyPages = 0;
    for (let p = 1; p <= doc.numPages; p++) {
      const page = await doc.getPage(p);
      try {
        const content = await page.getTextContent();
        const mapped = itemsFromTextContent(content.items, p);
        if (mapped.length === 0) emptyPages += 1;
        for (const item of mapped) items.push(item);
      } finally {
        page.cleanup();
      }
      onProgress?.({ page: p, pages: doc.numPages });
    }
    return { ...layout(name, items, doc.numPages), emptyPages };
  } finally {
    // Destroying the LOADING TASK is what tears the worker down in pdf.js 6; the
    // document proxy has no `destroy` of its own any more. Skipping it leaks a
    // worker per file, which a folder of PDFs turns into a stall.
    await task.destroy();
  }
}
